import React from "react";

interface ShimmerProps {
  className?: string;
  lines?: number;
  seed?: string | number;
}

// Simple string hash function to convert a string to a number
const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
};

// Function to generate a deterministic width based on a seed and line number
const getLineWidth = (seed: string | number, lineIndex: number): string => {
  // Convert seed to number if it's a string
  const numericSeed = typeof seed === "string" ? hashString(seed) : seed;
  
  // Use a simple hash function with seed and lineIndex to get a stable value
  const hash = (numericSeed * 17 + lineIndex * 31) % 100;
  
  if (hash < 20) return "w-1/2"; // 20% chance for half width
  if (hash < 40) return "w-3/4"; // 20% chance for 3/4 width
  if (hash < 60) return "w-2/3"; // 20% chance for 2/3 width
  if (hash < 80) return "w-5/6"; // 20% chance for 5/6 width
  return "w-full";   // 20% chance for full width
};

const SingleShimmer: React.FC<{ className?: string }> = ({ className = "h-4 w-full" }) => {
  return (
    <div
      className={`relative overflow-hidden bg-gray-200 dark:bg-slate-700 rounded animate-pulse ${className}`}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-slate-700 dark:via-slate-600 dark:to-slate-700 animate-shimmer"></div>
    </div>
  );
};

const Shimmer: React.FC<ShimmerProps> = ({ 
  className,
  lines = 1, 
  seed = String(Math.floor(Math.random() * 10000))
}) => {
  // If it's a single line shimmer, render the original component
  if (lines === 1) {
    return <SingleShimmer className={className} />;
  }
  
  // Generate multiple lines with different widths
  return (
    <div className="space-y-3">
      {Array.from({ length: lines }).map((_, index) => (
        <SingleShimmer 
          key={`shimmer-${seed}-${index}`} 
          className={`h-4 ${getLineWidth(seed, index)} ${className || ""}`}
        />
      ))}
    </div>
  );
};

export { Shimmer };
